// src/api/api.js

import axios from 'axios';
import { toast } from 'react-toastify';
import { getToken, clearUserSession, getUserType, getActorAccountIdFromToken } from './auth-helpers';
import logger from '../utils/logger'; // adjust path if needed
import { getActiveAccountId, getStoredAccountId } from './account-context';
import { shouldAttachLocalDevTenantHostHeader, TV_DEV_APP_HOST_HEADER, shouldAttachCrossOriginTenantAppHostHeader, TV_TENANT_APP_HOST_HEADER } from './local-dev-tenant-host';

const LAST_API_ERROR_TOAST_KEY = "__TV_LAST_API_ERROR_TOAST__";
const API_ERROR_DUPLICATE_WINDOW_MS = 1200;

const getLastApiErrorToast = () => {
    if (typeof window === "undefined") return null;
    return window[LAST_API_ERROR_TOAST_KEY] || null;
};

const markApiErrorToast = (message) => {
    if (typeof window === "undefined") return;
    window[LAST_API_ERROR_TOAST_KEY] = {
        timestamp: Date.now(),
        message: message || ""
    };
};

// Guard against duplicate local error toasts fired immediately after interceptor toasts.
if (!toast.__tvApiDuplicateGuardPatched) {
    const originalToastError = toast.error.bind(toast);
    toast.error = (content, options = {}) => {
        const lastApiToast = getLastApiErrorToast();
        const isFromApiInterceptor = Boolean(options.__fromApiInterceptor);
        if (
            !isFromApiInterceptor &&
            lastApiToast &&
            Date.now() - lastApiToast.timestamp <= API_ERROR_DUPLICATE_WINDOW_MS
        ) {
            return null;
        }
        return originalToastError(content, options);
    };
    toast.__tvApiDuplicateGuardPatched = true;
}


const API_URL = process.env.REACT_APP_API_URL || "";

const api = axios.create({
    baseURL: API_URL,
    withCredentials: true
});

const ACCOUNT_SKIP_PATHS = ['/auth/', '/ui-logs', '/health', '/fonts/list'];

/** Auth endpoints where 401 means invalid credentials, not an expired session. */
const AUTH_PUBLIC_PATHS = [
    '/auth/login',
    '/auth/forgot-password',
    '/auth/reset-password',
    '/auth/send-otp',
    '/auth/verify-otp',
    '/auth/fetchInvitedetails',
    '/auth/actor-invite-preview',
    '/auth/self-signup/request',
    '/auth/self-signup/verify',
];

export const isUnauthenticatedAuthRequest = (url = "") =>
    AUTH_PUBLIC_PATHS.some((path) => url.includes(path));
const ACCOUNT_QUERY_INJECTION_SKIP_PREFIXES = ['/assets'];
const ACCOUNT_SCOPED_PATH_PREFIXES = [
    '/campaigns',
    '/draft-campaigns',
    '/campaign-drafts',
    '/rule-engine',
    '/ca-videos',
    '/video-generation/v3',
    '/tags',
    '/assets',
    '/users',
    '/actors',
    '/campaign-actors',
    '/consumption',
    '/credit',
    '/payments',
    '/video-analytics',
    '/translator',
    '/media-enhancer',
    '/media',
    '/network-access-management',
    '/fileInfo',
    '/scale',
    '/config',
    '/fonts',
];

const shouldSkipAccountInjection = (url = "") => {
    return ACCOUNT_SKIP_PATHS.some((path) => url.includes(path));
};

const injectAccountId = (config) => {
    const method = String(config.method || "get").toLowerCase();
    const url = config.url || "";

    if (shouldSkipAccountInjection(url)) {
        return config;
    }

    const { path } = splitUrlAndQuery(url);
    const normalizedPath = path.startsWith("/") ? path : `/${path}`;
    // Account is already in the path (e.g. /accounts/45/shopify/shoppers); do not add account_id query param.
    if (normalizedPath.startsWith("/accounts/")) {
        return config;
    }
    if (ACCOUNT_QUERY_INJECTION_SKIP_PREFIXES.some((prefix) => normalizedPath === prefix || normalizedPath.startsWith(`${prefix}/`))) {
        return config;
    }

    const pathname = typeof window !== "undefined" ? window.location?.pathname || "/" : "/";
    const accountId = resolveAccountIdForRequest(pathname);
    if (!accountId) {
        return config;
    }

    if (method === "get" || method === "delete") {
        config.params = config.params || {};
        if (!config.params.account_id) {
            config.params.account_id = accountId;
        }
        return config;
    }

    if (config.data instanceof FormData) {
        if (!config.data.has("account_id")) {
            config.data.append("account_id", accountId);
        }
        return config;
    }

    if (config.data && typeof config.data === "object" && Array.isArray(config.data) === false) {
        if (!Object.hasOwn(config.data, "account_id")) {
            config.data.account_id = accountId;
        }
    }

    return config;
};

const isAbsoluteUrl = (url = "") => /^https?:\/\//i.test(url);

const splitUrlAndQuery = (url = "") => {
    const queryIndex = url.indexOf("?");
    if (queryIndex === -1) {
        return { path: url, query: "" };
    }
    return {
        path: url.slice(0, queryIndex),
        query: url.slice(queryIndex),
    };
};

const shouldScopeWithAccount = (path = "") => {
    return ACCOUNT_SCOPED_PATH_PREFIXES.some((prefix) => path === prefix || path.startsWith(`${prefix}/`));
};

const resolveAccountIdForRequest = (pathname = "/") => {
    const routeAccountId = getActiveAccountId(pathname);
    if (routeAccountId) {
        return routeAccountId;
    }
    if (getUserType() === "actor") {
        return getActorAccountIdFromToken();
    }
    // Legacy editor URLs (/edit-draft-campaigns/:id) omit account in the path; use persisted context.
    return getStoredAccountId() || null;
};

const injectAccountPathPrefix = (config) => {
    const url = config.url || "";
    if (!url || isAbsoluteUrl(url) || url.startsWith("/accounts/")) {
        return config;
    }

    const pathname = typeof window !== "undefined" ? window.location?.pathname || "/" : "/";
    const accountId = resolveAccountIdForRequest(pathname);
    if (!accountId) {
        return config;
    }

    const { path, query } = splitUrlAndQuery(url);
    const normalizedPath = path.startsWith("/") ? path : `/${path}`;
    if (!shouldScopeWithAccount(normalizedPath)) {
        return config;
    }

    config.url = `/accounts/${accountId}${normalizedPath}${query}`;
    return config;
};

// Request Interceptor
api.interceptors.request.use(
    (config) => {
        const token = getToken();
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        if (typeof window !== "undefined" && shouldAttachLocalDevTenantHostHeader(API_URL, window.location.hostname)) {
            config.headers[TV_DEV_APP_HOST_HEADER] = window.location.hostname;
        } else if (typeof window !== "undefined" && shouldAttachCrossOriginTenantAppHostHeader(API_URL, window.location.hostname)) {
            config.headers[TV_TENANT_APP_HOST_HEADER] = window.location.hostname;
        }
        config = injectAccountPathPrefix(config);
        config = injectAccountId(config);
        logger.info("API Request", {
            method: config.method,
            url: config.url,
            data: config.data,
            headers: config.headers
        });
        return config;
    },
    (error) => {
        logger.error("API Request Error", error);
        return Promise.reject(error);
    }
);

// Response Interceptor
api.interceptors.response.use(
    (response) => {
        const { url, method } = response.config;

        logger.info("API Response", {
        method,
        url,
        status: response.status,
        data: response.data
    });

        if (url.includes('/actors/') && response.data?.message === "Actor deleted successfully") {
            toast.success("Actor deleted successfully.");
        }

        if (url.includes('/accounts/') && response.data?.message === "Account deleted successfully") {
            toast.success("Account deleted successfully.");
        }

        return response;
    },
    (error) => {
        if (
            error?.config?.suppressErrorToast === true ||
            isUnauthenticatedAuthRequest(error.config?.url || "")
        ) {
            return Promise.reject(error);
        }
        const status = error.response?.status;
        const message = error.response?.data?.message || error.response?.data?.details || error.response?.data?.error || error.message;
        const isTimeoutError = error.code === "ECONNABORTED" || /timeout/i.test(error.message || "");
        const isNetworkError =
            !error.response &&
            (error.code === "ERR_NETWORK" ||
                /network\s?error/i.test(error.message || "") ||
                /failed to fetch/i.test(error.message || ""));

        logger.error("API Error", {
        status,
        message,
        method: error.config?.method,
        url: error.config?.url,
        data: error.config?.data,
        response: error.response?.data
    });

        if (isTimeoutError || isNetworkError) {
            const fallbackMessage =
                typeof navigator !== "undefined" && navigator.onLine === false
                    ? "You appear to be offline. Please check your internet connection."
                    : "Unable to reach the server. Please try again.";

            toast.error(fallbackMessage, { __fromApiInterceptor: true });
            markApiErrorToast(fallbackMessage);
            return Promise.reject(error);
        }

        switch (status) {
            case 202:
                toast.error(message || "Request accepted but processing is not complete yet.", { __fromApiInterceptor: true });
                markApiErrorToast(message);
                break;
            case 400:
                toast.error(message || "Bad request. Please check your input.", { __fromApiInterceptor: true });
                markApiErrorToast(message);
                break;
            case 401:
                toast.error(message || "Session expired. Please login again.", { __fromApiInterceptor: true });
                markApiErrorToast(message);
                clearUserSession();
                window.location.href = "/login";
                break;
            case 403:
                toast.error(message || "Access denied. You don't have permission.", { __fromApiInterceptor: true });
                markApiErrorToast(message);
                break;
            case 404: {
                const lastApiToast = getLastApiErrorToast();
                const toastMessage = message || "Requested resource not found.";
                const isDuplicateAccountNotFound =
                    /account not found/i.test(toastMessage)
                    && lastApiToast
                    && lastApiToast.message === toastMessage
                    && Date.now() - lastApiToast.timestamp <= API_ERROR_DUPLICATE_WINDOW_MS;
                if (!isDuplicateAccountNotFound) {
                    toast.error(toastMessage, { __fromApiInterceptor: true });
                    markApiErrorToast(toastMessage);
                }
                break;
            }
            case 429:
                toast.error(message || "Too many requests. Try again later.", { __fromApiInterceptor: true });
                markApiErrorToast(message);
                break;
            case 500:
                toast.error(message || "Server error occurred. Please try again later.", { __fromApiInterceptor: true });
                markApiErrorToast(message);
                break;
            case 502:
            case 503:
                toast.error(message || "Service temporarily unavailable.", { __fromApiInterceptor: true });
                markApiErrorToast(message);
                break;
            default:
                if (status >= 400) {
                    toast.error(message || "An unexpected error occurred.", { __fromApiInterceptor: true });
                    markApiErrorToast(message);
                }
                break;
        }

        return Promise.reject(error);
    }
);

export default api;
